import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix';
import React, { useEffect, useRef, useState } from 'react';
import { Button, Form, OverlayTrigger, Tooltip } from 'react-bootstrap';

export type DropdownOption = {
  id?: string;
  label: string;
  value: string;
};

interface Props {
  disabled?: boolean;
  highlightFirst?: boolean;
  isInvalid: boolean;
  label: string;
  name: string;
  onChange: (e: { name: string; value?: string }) => void;
  options: DropdownOption[];
  placeholder: string;
  required?: boolean;
  searchable?: boolean;
  value?: string;
}

const Dropdown: React.FC<Props> = ({
  disabled = false,
  highlightFirst = false,
  isInvalid,
  label,
  name,
  onChange,
  options,
  placeholder,
  required,
  searchable = false,
  value,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const { t } = useTranslationWithPrefix('views.process.personalData');

  const selectedOption = options.find(option => option.value === value);
  const selectedOptionLabel = selectedOption?.label;

  const filteredOptions = searchable
    ? options.filter(option => option.label.toLowerCase().includes(searchTerm.toLowerCase()))
    : options;

  const handleSelect = (option: DropdownOption) => {
    onChange({ name, value: option.id || option.value });
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleToggle = () => {
    if (disabled) return;
    setIsOpen(!isOpen);
    if (searchable) {
      setSearchTerm('');
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        if (searchable) {
          setSearchTerm('');
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [searchable]);

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;
    if (isOpen && searchable && searchInputRef.current) {
      timeoutId = setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
    return () => clearTimeout(timeoutId);
  }, [isOpen, searchable]);

  return (
    <>
      <label className={`small pb-1 ${disabled ? 'text-muted' : ''}`}>
        {label}
        {required ? '*' : ''}
      </label>
      <div
        className="dropdown w-full"
        ref={dropdownRef}
        style={{ overflow: 'visible', position: 'relative' }}
      >
        <Button
          aria-expanded={isOpen}
          className={`btn btn-dropdown dropdown-toggle w-full text-left ${
            selectedOptionLabel ? '' : 'empty'
          } ${isInvalid ? 'invalid' : ''} ${disabled ? 'disabled' : ''}`}
          disabled={disabled}
          id={name}
          onClick={handleToggle}
        >
          <span
            style={{
              display: 'block',
              maxWidth: 'calc(100% - 20px)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {selectedOptionLabel || placeholder}
          </span>
        </Button>

        {isOpen && (
          <div
            className="dropdown-menu show"
            style={{
              display: 'block',
              maxHeight: '350px',
              maxWidth: 'max-content',
              minWidth: '100%',
              overflow: 'hidden',
              width: '100%',
              zIndex: 1050,
            }}
          >
            {searchable && (
              <div className="px-3 py-2 border-bottom">
                <Form.Control
                  onChange={handleSearchChange}
                  placeholder={t('dropdown.search')}
                  ref={searchInputRef}
                  required={required}
                  size="sm"
                  type="text"
                  value={searchTerm}
                />
              </div>
            )}

            <div
              style={{
                maxHeight: searchable ? '250px' : '300px',
                overflowY: 'auto',
              }}
            >
              {filteredOptions.length > 0 ? (
                filteredOptions.map(option => (
                  <OverlayTrigger
                    delay={{ hide: 100, show: 500 }}
                    key={option.value}
                    overlay={<Tooltip id={`tooltip-${option.value}`}>{option.label}</Tooltip>}
                    placement="right"
                  >
                    <Button
                      className={`dropdown-item text-left w-100 border-0 ${
                        selectedOption?.value === option.value ? 'active' : ''
                      } ${
                        highlightFirst && filteredOptions.indexOf(option) === 0 ? 'fw-bold' : ''
                      }`}
                      onClick={() => handleSelect(option)}
                      style={{
                        backgroundColor:
                          highlightFirst && filteredOptions.indexOf(option) === 0
                            ? 'var(--bs-light)'
                            : undefined,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {option.label}
                    </Button>
                  </OverlayTrigger>
                ))
              ) : (
                <div className="px-3 py-2 text-muted">{t('dropdown.noResults')}</div>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Dropdown;
